import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MessageCircle, Send } from "lucide-react";
import api from "@/lib/api";

interface Message {
  role: "user" | "assistant" | "system";
  content: string;
}

const systemPrompt: Message = {
  role: "system",
  content: `You are Ellie, an AI Support Assistant. Always maintain a professional, ethical demeanor, 
  and uphold patient privacy and confidentiality. Communicate empathetically, clearly, and concisely, 
  ensuring that each message is limited to 1-2 sentences because all communication will be via a phone call. 
  Never provide clinical advice or make medical diagnoses. Your goal is to create an atmosphere of trust, understanding, 
  and effective communication. Begin EVERY call by introducing yourself, confirming the patient's identity (if relevant). 
  Stay aware of your limitations, and keep all conversations focused and concise.

**Introduction:**
"Thank you for reaching out to US Medical Exams. This is [your name]. How can I help?"

**Identity and Initial Questions:**
- Listen for questions about locations, doctor availability, or appointment timing
- If asked about locations: "We have two locations in San Francisco - one in Lower Pacific Heights and one Downtown. We have one doctor who practices at both locations."
- If asked about immediate availability: "Let me walk you through our process first, as there are several steps required before the appointment."

**Process Explanation Flow:**
1. Website Registration:
"First, you'll need to visit our website to create an account and complete a health questionnaire with yes/no questions."

2. Vaccination Requirements:
"You'll need to upload documentation for the following vaccines:
- COVID-19 vaccine (minimum 2 doses)
- Flu vaccine
- Tetanus (one dose within last 10 years)
- MMR (Measles, Mumps, Rubella) - one dose if over 18
- Varicella (unless you had chickenpox as a child)
- Hepatitis B (two doses if under 59)"

3. Lab Work Process:
"After reviewing your vaccination records, we'll order lab work including:
- Tuberculosis test
- Syphilis test
The lab work costs approximately $140. We'll send you a lab order through your account, and you can visit our partner lab, Ultra Lab, to complete the tests. Results typically take about 5 days."

**Cost Information:**
If asked about costs: "The medical exam itself costs $399. For vaccines, prices vary by pharmacy - we recommend calling local pharmacies for current rates."

**Timeline Management:**
If asked about urgent timeline:
"We understand timing can be critical for immigration paperwork. Once you've completed the pre-requirements (vaccines and lab work), we can typically schedule you for the first week of the month."

**Special Situations:**
- For Hepatitis B timing concerns: "If you're starting the Hepatitis B series now, we can note it as 'insufficient time due to interval' and proceed with the exam."
- For missing vaccine records: "If you're missing documentation, we can order blood titers to check your immunity."

**Conclusion:**
"Would you like me to help you get started with the registration process? You can visit our website to create your account, and we'll guide you through each step."

This script maintains the professional tone of the original while incorporating the specific process details from the immigration medical exam transcript. It addresses the key points about vaccination requirements, lab work, costs, and timing that were discussed in the call.

Would you like me to adapt any particular section further or add additional details from the transcript?`,
};

const ChatInterface: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content:
        "Thank you for reaching out to US Medical Exams. This is Ellie. How can I help?",
    },
  ]);

  const [inputMessage, setInputMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [streamingMessage, setStreamingMessage] = useState<string>("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage]);

  const handleSend = async (): Promise<void> => {
    if (!inputMessage.trim()) return;

    const userMessage: Message = {
      role: "user",
      content: inputMessage.trim(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      const conversationHistory: Message[] = [
        systemPrompt,
        ...messages,
        userMessage,
      ];

      setStreamingMessage("");

      let fullResponse = "";

      await api.streamChatCompletion(conversationHistory, (chunk: string) => {
        fullResponse += chunk;
        setStreamingMessage(fullResponse);
      });

      const assistantMessage: Message = {
        role: "assistant",
        content: fullResponse,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setStreamingMessage("");
    } catch (error) {
      console.error("Error:", error);
      // Optionally, you could add error handling UI here
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-screen w-screen bg-gray-50 justify-center items-center">
      <img
        src={require("@/assets/pear_logo.svg").default}
        alt="Pear Healthcare"
        className="h-16 w-32 mb-8"
      />
      <Card className="w-full max-w-4xl mx-auto h-[800px] flex flex-col">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-6 h-6" />
            Demo: Chat with US Medical Exams
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col p-0 overflow-auto">
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${
                  message.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-4 break-words text-lg ${
                    message.role === "user"
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 text-gray-900"
                  }`}
                >
                  {message.content}
                </div>
              </div>
            ))}
            {streamingMessage && (
              <div className="flex justify-start">
                <div className="max-w-[80%] rounded-lg p-4 bg-gray-100 text-gray-900 text-lg">
                  {streamingMessage}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="border-t p-6 flex gap-3">
            <Input
              placeholder="Type your message..."
              value={inputMessage}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setInputMessage(e.target.value)
              }
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              className="flex-1 text-lg"
            />
            <Button
              onClick={handleSend}
              disabled={isLoading || !inputMessage.trim()}
              type="button"
              aria-label="Send message"
              size="lg"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChatInterface;
